import { motion } from 'framer-motion';
import { Shield, Lock, Eye, FileText } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const sections = [
  {
    title: 'Information We Collect',
    icon: FileText,
    content: [
      { subtitle: 'Personal Information', text: 'When you use MeritX or our other services, we collect information that you provide directly to us, including name, email address, phone number, educational details, and any other information you choose to provide when using the MeritX app, our services, or contacting us.' },
      { subtitle: 'Usage Information', text: 'When you use MeritX, we automatically collect certain information about your device and how you interact with the app, including device type, operating system, app usage patterns, test performance data, practice session details, and analytics to improve your learning experience.' },
      { subtitle: 'Cookies and Tracking', text: 'We use cookies and similar tracking technologies to track activity on our service and store certain information to improve user experience and analyze usage patterns.' },
    ],
  },
  {
    title: 'How We Use Your Information',
    icon: Shield,
    content: [
      { subtitle: 'Service Delivery', text: 'We use your information to provide, maintain, and improve the MeritX app and our other services, including delivering AI-powered mock tests, performance analysis, ranking features, personalized learning recommendations, and technical support.' },
      { subtitle: 'Communication', text: 'To send you technical notices, updates, security alerts, and support messages, as well as to respond to your inquiries and fulfill your requests.' },
      { subtitle: 'Analytics and Improvement', text: 'To monitor and analyze trends, usage, and activities in connection with our services to improve functionality and user experience.' },
      { subtitle: 'Marketing', text: 'With your consent, we may send you promotional communications about our services, events, and other information we think may interest you.' },
    ],
  },
  {
    title: 'Information Sharing and Disclosure',
    icon: Eye,
    content: [
      { subtitle: 'Service Providers', text: 'We may share your information with third-party service providers who perform services on our behalf, such as hosting, data analysis, payment processing, and customer service.' },
      { subtitle: 'Business Transfers', text: 'We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business.' },
      { subtitle: 'Legal Requirements', text: 'We may disclose your information if required to do so by law or in response to valid requests by public authorities.' },
      { subtitle: 'Consent', text: 'We may share your information with your consent or at your direction.' },
    ],
  },
  {
    title: 'Data Security',
    icon: Lock,
    content: [
      { subtitle: 'Security Measures', text: 'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.' },
      { subtitle: 'Data Retention', text: 'We retain your personal information for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required or permitted by law.' },
      { subtitle: 'Your Responsibility', text: 'Please note that no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee its absolute security.' },
    ],
  },
];

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#060D1F]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 bg-[#060D1F] overflow-hidden">
        <div className="absolute top-10 right-20 w-72 h-72 bg-orange-500/15 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 left-20 w-56 h-56 bg-blue-600/15 rounded-full blur-[60px]" />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '36px 36px' }} />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 pt-20 pb-24 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Shield className="w-4 h-4" /> Legal · Privacy
            </span>
            <h1 className="text-5xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              How CALLOC Technologies and the MeritX app collect, use, and protect your information.
            </p>
            <p className="text-slate-500 text-sm mt-4">Last Updated: January 25, 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 space-y-8">

        {/* intro */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="bg-orange-500/10 border border-orange-500/20 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-white mb-3">Welcome</h2>
          <p className="text-slate-300 leading-relaxed mb-3">
            CALLOC Technologies Private Limited ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services, including our <strong className="text-white">MeritX</strong> mobile application, website, and other software development services.
          </p>
          <p className="text-slate-300 leading-relaxed">
            Please read this privacy policy carefully. If you do not agree with its terms, please do not access our services or use the MeritX app.
          </p>
        </motion.div>

        {/* sections */}
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <motion.div key={index} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="bg-[#0C1628] border border-white/10 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center">
                  <Icon className="w-5 h-5 text-orange-400" />
                </div>
                <h2 className="text-xl font-bold text-white">{section.title}</h2>
              </div>
              <div className="space-y-5">
                {section.content.map((item, idx) => (
                  <div key={idx}>
                    <h3 className="font-semibold text-white mb-1">{item.subtitle}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })}

        {/* Your Rights */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="bg-[#0C1628] border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-white mb-6">Your Rights and Choices</h2>
          <div className="space-y-5">
            {[
              { title: 'Access and Update', text: 'You may access and update your personal information by contacting us at officecalloc@gmail.com.' },
              { title: 'Opt-Out', text: 'You may opt out of receiving promotional communications from us by following the unsubscribe instructions in those communications or by contacting us directly.' },
              { title: 'Data Deletion', text: 'You may request deletion of your MeritX account and personal information by contacting officecalloc@gmail.com or by visiting our account deletion page. We will process your request within 30 days.' },
            ].map((item) => (
              <div key={item.title}>
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Children */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="bg-[#0C1628] border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-white mb-3">Children's Privacy</h2>
          <p className="text-slate-400 text-sm leading-relaxed">MeritX and our services are not intended for children under 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe we may have collected such information, contact us immediately at officecalloc@gmail.com.</p>
        </motion.div>

        {/* Contact */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="bg-[#080F1E] border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-white mb-4">Contact Us</h2>
          <p className="text-slate-400 text-sm mb-6">If you have questions about this Privacy Policy or the MeritX app, reach out:</p>
          <div className="space-y-2 text-sm text-slate-400">
            {[
              ['App Name', 'MeritX — Smart Practice. High Rank'],
              ['Developer', 'CALLOC Technologies Private Limited'],
              ['Email', 'officecalloc@gmail.com'],
              ['Phone', '+91 8971504091'],
              ['Address', 'Mysore, Karnataka, India'],
              ['Website', 'www.calloctech.com'],
            ].map(([k, v]) => (
              <p key={k}><span className="text-white font-medium">{k}:</span> {v}</p>
            ))}
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
